import React, { createContext, useEffect, useContext, useRef } from 'react';
import { ethers, Signer, utils } from 'ethers';
import { useAuth } from './auth.context';
import Web3 from 'web3';
import { getChainConfig } from 'utils';
import {
  // GetAppConfigByKeyRequest,
  TokenCriteria,
} from 'core/models/app-config/get-app-config-by-key.model';
import BionicABI from '../assets/ABI/Bionic.abi.json';
// import erc20Abi from '../components/io/erc20.json';
// import { mapDecimalToUnit } from 'utils/format';
import { useNotify } from './notify.context';
// import { AppConfigurationKey } from 'core/models/app-config/get-app-config.model';
// import { AppConfigV1BaseServerRepo } from 'core/repository/base-server/v1/app-config.repo';
import { usePublicClient } from 'wagmi';
import { formatUnits } from 'viem';
import useNotifyStore from 'stores/notifyStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from 'components/profile/data/wallets';
import { WalletType } from 'core/models/users/wallet/wallet-address.model';
import BPToast from 'components/BionicUI/toast/BPToast';
import { ROUTES } from 'routes';
import { useProfile } from 'components/profile/data/profile';
interface connectToWalletResponse {
  success: boolean;
  account?: string;
  signer?: ethers.providers.JsonRpcSigner | null;
}

type ChainConfig = {
  id: number;
  name: string;
  network?: string; // Optional property
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  rpcUrls: {
    [key: string]: { http?: string[]; webSocket?: string[] }; // Key represents provider name (e.g., "alchemy")
  };
  blockExplorers: {
    [key: string]: { name: string; url: string }; // Key represents explorer name (e.g., "etherscan")
  };
  // Additional properties can be added here if needed
  testnet?: boolean; // Optional property
};

export interface WatchAsset {
  type: 'ERC20' | 'ERC721' | 'ERC1155';
  options: {
    address: string;
    symbol: string;
    decimals: number;
    image?: string;
  };
}

interface Caveat {
  type: string;
  value: string[];
}
interface ErrorResponse {
  code: number;
  message: string;
  data: {
    code: number;
    message: string;
    data: string; // Assuming data is a hex string
    cause: null;
  };
}
export interface Permission {
  id: string;
  parentCapability: string;
  invoker: string;
  caveats: Caveat[];
  date: number;
}

interface EthContextProps {
  refreshWalletConnection: () => Promise<boolean>;
  setUserBalance: React.Dispatch<React.SetStateAction<number>>;
  connectToWallet: () => Promise<connectToWalletResponse>;
  disconnectWallet: () => void;
  requestWalletConnection: () => Promise<connectToWalletResponse>;
  handleProviderError: (error: unknown) => string | undefined;
  requestChangeChain: (chainId: number) => Promise<void>;
  requestAddNewChain: (chainId: number) => Promise<void>;
  requestWatchAsset: (asset: WatchAsset) => Promise<void>;
  connectUsingWalletConnect: () => Promise<void>;
  getWalletBcnxBalance: (
    tokenCriteria: TokenCriteria,
    walletAddress: string
  ) => Promise<number>;
}

export const EthContext = createContext<EthContextProps | undefined>(undefined);
EthContext.displayName = 'EthContext';

export const useEth = (): EthContextProps => {
  const context = useContext(EthContext);
  if (!context) {
    throw new Error('useEth must be used within an EthProvider');
  }
  return context;
};

export const EthProvider = (props: any) => {
  const { isLoggedIn, user } = useAuth();
  const signerRef = useRef<Signer | null>(null);
  const accountRef = useRef<string | null>(null); // for chain function call on verification.
  const { notifyError } = useNotify();
  const publicClient = usePublicClient();
  const { setCurrentToast } = useNotifyStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [currencies] = useWallet(WalletType.SMART);
  const { profile } = useProfile();

  const connectToWallet = async (): Promise<connectToWalletResponse> => {
    const ethereum = (window as any)
      .ethereum as ethers.providers.ExternalProvider;
    if (ethereum && typeof ethereum.request === 'function') {
      try {
        const accounts = await ethereum.request({
          method: 'eth_requestAccounts',
        });
        const provider = new ethers.providers.Web3Provider(ethereum);
        const signer = provider.getSigner();
        signerRef.current = signer;

        const formattedAccount = utils.getAddress(accounts[0]); // default account will be lowercase,
        // and verification will fail on backend.
        accountRef.current = formattedAccount; // for verification function, need to
        return {
          success: true,
          account: accountRef.current,
          signer,
        };
      } catch (error) {
        console.error('Error connecting to wallet:', error);
        signerRef.current = null;
      }
    } else {
      console.error('Metamask not detected');
      notifyError('Metamask not detected');
    }
    return {
      success: false,
    };
  };

  const refreshWalletConnection = async () => {
    return connectToWallet();
  };

  // when user signs out, disconnect wallet
  const disconnectWallet = () => async () => {
    signerRef.current = null;
    accountRef.current = null;
  };

  const requestChangeChain = async (chainId: number) => {
    // chainIdHex should be in hex string format
    const ethereum = (window as any)
      .ethereum as ethers.providers.ExternalProvider;
    if (ethereum && typeof ethereum.request === 'function') {
      try {
        await ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: Web3.utils.toHex(chainId) }],
        });
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          error.code === 4902
        ) {
          requestAddNewChain(chainId);
        }
      }
    }
  };

  const requestAddNewChain = async (chainId: number) => {
    const ethereum = (window as any)
      .ethereum as ethers.providers.ExternalProvider;
    if (ethereum && typeof ethereum.request === 'function') {
      const chainConfig = getChainConfig(chainId) as unknown as ChainConfig;
      await ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: Web3.utils.toHex(chainId),
            chainName: chainConfig?.name,
            rpcUrls: chainConfig?.rpcUrls['default'].http,
            iconUrls: [],
            nativeCurrency: chainConfig?.nativeCurrency,
            blockExplorerUrls: [chainConfig?.blockExplorers['default'].url], // Add the missing property 'blockExplorers'
          },
        ],
      });
    }
  };

  const handleProviderError = (error: unknown): string | undefined => {
    console.error(error);
    if (typeof error === 'object' && error !== null) {
      if ('code' in error && 'message' in error) {
        return (error as ErrorResponse).message;
      }

      if ('error' in error) {
        const errorObject = error.error;
        // Check if errorObject has expected properties
        if (
          typeof errorObject === 'object' &&
          errorObject !== null &&
          'message' in errorObject &&
          'reason' in errorObject &&
          'code' in errorObject &&
          'method' in errorObject &&
          'transaction' in errorObject
        ) {
          return errorObject?.message as string;
        }
      }
    }
    return undefined;
  };

  const requestWatchAsset = async (asset: WatchAsset) => {
    const ethereum = (window as any)
      .ethereum as ethers.providers.ExternalProvider;
    if (ethereum && typeof ethereum.request === 'function') {
      await ethereum.request({
        method: 'wallet_watchAsset',
        params: [asset || undefined],
      });
      console.log('Successfully requested to add ERC20 token to MetaMask.');
    }
  };

  const requestWalletConnection =
    async (): Promise<connectToWalletResponse> => {
      try {
        const ethereum = (window as any)
          .ethereum as ethers.providers.ExternalProvider;
        if (ethereum && typeof ethereum.request === 'function') {
          const account = (await ethereum.request({
            method: 'wallet_requestPermissions',
            params: [
              {
                eth_accounts: {},
              },
            ],
          })) as Permission[];
          if (account[0]?.caveats[0]?.value[0]) {
            const provider = new ethers.providers.Web3Provider(ethereum);
            const signer = provider.getSigner();
            signerRef.current = signer;
            accountRef.current = account[0]?.caveats[0]?.value[0];

            return {
              success: true,
              account: accountRef.current,
              signer,
            };
          }
        }
      } catch (error) {
        console.error('Error connecting to wallet:', error);
        return {
          success: false,
        };
      }
      return {
        success: false,
      };
    };

  async function getWalletBcnxBalance(
    tokenCriteria: TokenCriteria,
    walletAddress: string
  ): Promise<number> {
    // const web3 = new Web3(tokenCriteria?.endPoint);
    // const tokenContract = new web3.eth.Contract(
    // erc20Abi,
    //   BionicABI,
    //   tokenCriteria?.contractAddress
    // );
    const decimals = (await publicClient?.readContract({
      abi: BionicABI,
      address: tokenCriteria?.contractAddress,
      functionName: 'decimals',
    })) as number;

    // const decimals = (await contract.methods.decimals().call()) as number;
    //  const formattedAmount = parseUnits(
    //    amount.toString(),
    //    decimals
    //  );
    // const decimals = (await tokenContract.methods
    //   .decimals()
    //   .call()) as number;
    const balance = (await publicClient?.readContract({
      abi: BionicABI,
      address: tokenCriteria?.contractAddress,
      functionName: 'balanceOf',
      args: [walletAddress],
    })) as bigint;

    return Number(formatUnits(balance, decimals));

    // const decimals = (await tokenContract.methods.decimals().call()) as number;

    // const balance = await tokenContract.methods.balanceOf(walletAddress).call();
    // return Number(
    //   web3.utils.fromWei(
    //     balance as unknown as number,
    //     mapDecimalToUnit(decimals) as any
    //   )
    // );
  }

  //   const getTokenBalance = async (tokenAddress: Address, walletAddress: Address): Promise<bigint> => {
  //     if (!publicClient) return BigInt(0);
  //     try {
  //         const data = await publicClient?.readContract({
  //             address: tokenAddress,
  //             abi: erc20Abi,
  //             functionName: "balanceOf",
  //             args: [walletAddress]
  //         });

  //         return data as bigint;
  //     } catch (err: unknown) {
  //         if (err instanceof Error) {
  //             console.error(err.message);
  //         }
  //         return BigInt(0);
  //     }
  // };

  useEffect(() => {
    // console.log('currencies--',currencies,profile)
    if (
      profile &&
      profile?.onboardingPhase === 'DONE' &&
      currencies &&
      currencies[0].symbol === 'tBCNX' &&
      currencies[0].balance < 100000
    ) {
      // console.log('&&&&&&')
      setCurrentToast(
        <BPToast
          message="Insufficient balance"
          description="Deposit a minimum 100K $tBCNX into your smart wallet"
          variant="error"
        />
      );
      navigate(ROUTES.PROFILE);
    }
  }, [pathname, currencies, profile]);

  // if user sign out, disconnect wallet
  useEffect(() => {
    if (!isLoggedIn && !!!user) {
      disconnectWallet();
    }
  }, [user, isLoggedIn]);

  const value = {
    refreshWalletConnection,
    connectToWallet,
    disconnectWallet,
    requestWalletConnection,
    requestChangeChain,
    handleProviderError,
    requestAddNewChain,
    requestWatchAsset,
    getWalletBcnxBalance,
  };

  return <EthContext.Provider value={value} {...props} />;
};
